'use client';

import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useDropdownOptions } from '@/contexts/DropdownContext';

interface DropdownProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  dropdownKey: string;
  label?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  labelClassName?: string;
  selectClassName?: string;
  errorClassName?: string;
}

const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
  (
    {
      dropdownKey,
      label,
      placeholder = 'Select an option',
      error,
      required = false,
      className = '',
      labelClassName = '',
      selectClassName = '',
      errorClassName = '',
      ...props
    },
    ref
  ) => {
    const options = useDropdownOptions(dropdownKey);

    const baseSelectClasses = `
      w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg 
      focus:ring-2 focus:ring-primary-500 focus:border-transparent 
      transition-all duration-200 appearance-none bg-white
      ${error ? 'border-red-500 focus:ring-red-500' : ''}
      ${props.disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}
    `;

    const finalSelectClasses = `${baseSelectClasses} ${selectClassName}`.trim();

    return (
      <div className={`space-y-2 ${className}`}>
        {label && (
          <label
            className={`block text-sm font-medium text-gray-700 ${labelClassName}`}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          <select
            ref={ref}
            className={finalSelectClasses}
            {...props}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
        
        {error && (
          <p className={`text-sm text-red-600 ${errorClassName}`}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;
